import { logError, logInfo, logDebug, logWarn } from './utils/logger';
import { v1 as uuidv1 } from 'uuid';
import { RetryQueue, RetriableRequestOptions } from './retry-queue';
import { EventPersistence, QueuedEvent } from './persistence';
import type { ErrorReport } from './errors/error-payload';

// SDK version will be replaced at build time by Rollup replace plugin
const SDK_VERSION = '__SDK_VERSION__';

export const MAX_CHUNK_SIZE_BYTES = 1024 * 1024; // 1MB chunk size - more conservative
const KEEP_ALIVE_THRESHOLD = 64 * 1024 * 0.8; // 64KB * 0.8 for safety margin
const REQUEST_TIMEOUT_MS = 10000; // 10 seconds default timeout
// CORS-simple type. application/json forces a preflight sendBeacon cannot
// complete against Access-Control-Allow-Origin: *.
const BEACON_MIME = 'text/plain';

/**
 * Fire-and-forget POST that survives page unload, without navigator.sendBeacon's
 * spec-mandated `credentials: include` — which browsers reject outright against
 * this SDK's wildcard-origin CORS policy (arbitrary customer domains, no cookies
 * needed since auth travels in the body as apiKey). `fetch` + `keepalive` gives
 * the same unload-survival guarantee while letting us omit credentials.
 * Falls back to sendBeacon in environments without fetch/keepalive support.
 */
function sendKeepaliveBeacon(url: string, blob: Blob): boolean {
    if (typeof fetch === 'function') {
        fetch(url, {
            method: 'POST',
            body: blob,
            keepalive: true,
            credentials: 'omit',
        }).catch(() => {
            // Fire-and-forget: failures are silent, same as sendBeacon.
        });
        return true;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        return navigator.sendBeacon(url, blob);
    }
    return false;
}

// One encoder for the process instead of one per call — the old code allocated
// a TextEncoder per event, on the main thread, for no benefit.
//
// Created lazily rather than at module scope on purpose: this module is also
// imported in SSR/bundler contexts, and a module-level `new TextEncoder()` would
// turn a missing global into an import-time crash instead of the call-time one
// the previous code had. Same failure semantics, one allocation.
let textEncoder: TextEncoder | null = null;

function encodedByteLength(value: any): number {
    if (!textEncoder) textEncoder = new TextEncoder();
    return textEncoder.encode(safeJsonStringify(value)).length;
}

/**
 * Running byte total for the chunk being assembled.
 *
 * This replaces a per-event `isChunkSizeExceeded(currentChunk, event)` that
 * re-serialized and re-UTF8-encoded the ENTIRE accumulated chunk to decide
 * whether one more event fit. That made chunking O(N^2) in both time and
 * allocation for a chunk of N events, on the main thread, on every flush.
 * Measured on realistic rrweb DOM-mutation events (~424 B each):
 *
 *     events |  before |   after
 *        200 |    55 ms |  1.5 ms
 *        500 |   358 ms |  3.8 ms
 *       1000 |  1374 ms |  7.0 ms
 *       2000 |  5504 ms | 13.8 ms
 *
 * The queue flushes at ~800-1000 events (see MAX_QUEUE_SIZE in tracker.ts), and
 * p95 sessions carry ~10k rrweb events, so the 1000-event row was the steady
 * state on a busy page. Every flush was a long task by Google's 50 ms
 * definition, which lands on INP — a metric this SDK also reports.
 *
 * Byte-exactness matters, because the chunk boundaries this picks must match
 * what the old full-measure produced or payload sizes shift. The total is
 * therefore built from the same pieces JSON.stringify emits:
 *
 *   - `envelopeBytes` — `{"sessionId":"…","events":[]}` with an empty array.
 *   - each event's own encoded length.
 *   - one byte for the `,` separator before every event after the first.
 *
 * Verified against the previous implementation across 5 event-shape scenarios
 * (uniform small, uniform medium, mixed, few-huge, exact-boundary): identical
 * chunk boundaries in every case, no chunk over MAX_CHUNK_SIZE_BYTES. See
 * `__tests__/chunk-size.test.ts`, which pins that equivalence.
 */
export function createChunkByteCounter(sessionId: string) {
    const envelopeBytes = encodedByteLength({ sessionId, events: [] });
    let totalBytes = envelopeBytes;
    let eventCount = 0;

    // The caller always asks `wouldExceed(e)` and then commits the same `e` via
    // add() or reset([e]), so without a memo every event is serialized twice.
    // One slot, keyed by object identity — exact, and it falls back to a fresh
    // measure for any other call order.
    let lastEvent: any = null;
    let lastEventBytes = 0;

    function eventBytes(event: any): number {
        if (event !== lastEvent) {
            lastEvent = event;
            lastEventBytes = encodedByteLength(event);
        }
        return lastEventBytes;
    }

    return {
        /** Would adding `event` push the serialized chunk past the cap? */
        wouldExceed(event: any): boolean {
            const separator = eventCount > 0 ? 1 : 0;
            return totalBytes + eventBytes(event) + separator > MAX_CHUNK_SIZE_BYTES;
        },
        /** Account for an event that has been pushed onto the chunk. */
        add(event: any): void {
            totalBytes += eventBytes(event) + (eventCount > 0 ? 1 : 0);
            eventCount++;
        },
        /** Start a fresh chunk. */
        reset(events: any[] = []): void {
            totalBytes = envelopeBytes;
            eventCount = 0;
            for (const event of events) this.add(event);
        },
    };
}

export function validateSingleEventSize(event: any, sessionId: string): void {
    const singleEventSize = encodedByteLength({ sessionId, events: [event] });

    if (singleEventSize > MAX_CHUNK_SIZE_BYTES) {
        // Instead of throwing, log a warning and suggest reducing event size
        logWarn(`Single event size (${singleEventSize} bytes) exceeds maximum chunk size (${MAX_CHUNK_SIZE_BYTES} bytes). Consider reducing event data size.`);
    }
}





/**
 * Safe JSON stringify that handles BigInt values
 */
function safeJsonStringify(data: any): string {
    return JSON.stringify(data, (_, value) => {
        if (typeof value === 'bigint') {
            return value.toString();
        }
        return value;
    });
}

export function splitLargeEvent(event: any, sessionId: string): any[] {
    // ✅ SIMPLE VALIDATION
    if (!event || typeof event !== 'object') {
        return [];
    }
    
    const eventSize = encodedByteLength({ sessionId, events: [event] });

    if (eventSize <= MAX_CHUNK_SIZE_BYTES) {
        return [event];
    }

    // If event is too large, try to split it by removing large properties
    const simplifiedEvent = { ...event };
    
    // Remove potentially large properties
    const largeProperties = ['screenshot', 'html', 'dom', 'fullText', 'innerHTML', 'outerHTML'];
    largeProperties.forEach(prop => {
        if (simplifiedEvent[prop]) {
            delete simplifiedEvent[prop];
        }
    });

    // Check if simplified event is now small enough
    const simplifiedSize = encodedByteLength({ sessionId, events: [simplifiedEvent] });

    if (simplifiedSize <= MAX_CHUNK_SIZE_BYTES) {
        return [simplifiedEvent];
    }

    // If still too large, create a minimal event
    const minimalEvent = {
        type: event.type,
        timestamp: event.timestamp,
        url: event.url,
        pathname: event.pathname,
        // Keep only essential properties
        ...Object.fromEntries(
            Object.entries(event).filter(([key, value]) => 
                !largeProperties.includes(key) && 
                typeof value !== 'object' && 
                typeof value !== 'string' || 
                (typeof value === 'string' && value.length < 1000)
            )
        )
    };

    return [minimalEvent];
}

export class HumanBehaviorAPI {
    private apiKey: string;
    private baseUrl: string;
    private monthlyLimitReached: boolean = false;
    private throttledUntil: number = 0;
    private sessionId: string = '';
    private endUserId: string | null = null;
    private cspBlocked: boolean = false; // Track if CSP is blocking requests
    // Probation counter for the sticky beacon-only mode: reset on any
    // successful fetch, so one transient failure never degrades the session.
    private consecutiveFetchFailures: number = 0;
    private retryQueue: RetryQueue;
    private persistence: EventPersistence;
    private requestTimeout: number = REQUEST_TIMEOUT_MS;
    private currentBatchSize: number = 100; // Dynamic batch size for 413 handling
    private _isDrainingPersisted: boolean = false; // Guards against overlapping persisted-queue drains

    constructor({ apiKey, ingestionUrl }: { apiKey: string, ingestionUrl: string }) {
        this.apiKey = apiKey;
        this.baseUrl = ingestionUrl;
        this.persistence = new EventPersistence(apiKey);
        this.retryQueue = new RetryQueue((options) => this._sendRequestInternal(options));

        // Drain any events persisted by a previous session/tab, and again
        // whenever connectivity returns. The tracker also drives periodic
        // in-session drains via flushPersistedEvents() so a failed batch is not
        // stranded in storage until the next page load.
        this.flushPersistedEvents();
        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
            window.addEventListener('online', () => {
                this.flushPersistedEvents();
            });
        }
    }

    /**
     * Set session and user IDs for tracking context
     */
    public setTrackingContext(sessionId: string, endUserId: string | null): void {
        this.sessionId = sessionId;
        this.endUserId = endUserId;
    }

    /**
     * Drain the durable (localStorage) event queue: resend each persisted batch
     * oldest-first and remove it only once the server confirms receipt.
     *
     * Safe to call repeatedly — a concurrency guard prevents overlapping drains,
     * and a batch is removed only after a confirmed send, so nothing is lost on
     * failure. We stop at the first failure to preserve ordering and to back off
     * instead of hammering a still-down server (the caller retries on the next
     * flush tick / `online` event). Unlike sendEventsChunked, the send here never
     * re-persists on failure — the batch is already durable in storage.
     */
    public async flushPersistedEvents(): Promise<void> {
        if (this._isDrainingPersisted) {
            return;
        }
        if (!this.checkMonthlyLimit()) {
            return;
        }
        const persistedQueue = this.persistence.getQueue();
        if (persistedQueue.length === 0) {
            return;
        }

        this._isDrainingPersisted = true;
        try {
            logDebug(`Draining ${persistedQueue.length} persisted event batch(es) from storage`);
            for (const queuedEvent of persistedQueue) {
                const sent = await this._sendPersistedBatch(queuedEvent);
                if (!sent) {
                    // Leave this (and everything after it) in storage for a later drain.
                    break;
                }
                this.persistence.removeFromQueue(1);
            }
        } finally {
            this._isDrainingPersisted = false;
        }
    }

    /**
     * Send a single persisted batch exactly once. Returns true only when the
     * server confirms receipt. Never re-persists on failure (the batch is
     * already durable in storage) — the caller keeps it queued for the next
     * drain, so this cannot duplicate the localStorage entry.
     */
    private async _sendPersistedBatch(item: QueuedEvent): Promise<boolean> {
        if (this.isThrottled()) {
            return false; // stays queued; drained after the throttle window
        }
        try {
            const validEvents = (item.events || []).filter((e) => e && typeof e === 'object');
            if (validEvents.length === 0) {
                return true; // nothing to send — treat as drained
            }
            const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body: safeJsonStringify({
                    sessionId: item.sessionId,
                    events: validEvents,
                    endUserId: item.endUserId ?? null,
                    windowId: item.windowId,
                    automaticProperties: item.automaticProperties,
                    sdkVersion: SDK_VERSION
                })
            });

            if (!response.ok) {
                if (response.status === 429) {
                    await this._apply429(response);
                }
                return false;
            }

            try {
                const responseJson = await response.json();
                if (responseJson && responseJson.monthlyLimitReached === true) {
                    this.monthlyLimitReached = true;
                }
            } catch {
                // Non-JSON success body — fine, the send was accepted.
            }
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Internal method to send request (used by retry queue)
     */
    private async _sendRequestInternal(options: RetriableRequestOptions): Promise<void> {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        if (controller) {
            timeoutId = setTimeout(() => {
                controller!.abort();
            }, this.requestTimeout);
        }

        try {
            const estimatedSize = options.estimatedSize || 0;
            const useKeepalive = options.method === 'POST' && estimatedSize < KEEP_ALIVE_THRESHOLD;

            const response = await fetch(options.url, {
                method: options.method || 'GET',
                headers: options.headers || {},
                body: options.body,
                signal: controller?.signal,
                keepalive: useKeepalive
            });

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            const responseText = await response.text();
            let responseJson: any = null;
            
            try {
                responseJson = JSON.parse(responseText);
            } catch {
                // Not JSON, ignore
            }

            if (options.callback) {
                options.callback({
                    statusCode: response.status,
                    text: responseText,
                    json: responseJson
                });
            }

            if (!response.ok) {
                throw { status: response.status, message: responseText };
            }
        } catch (error: any) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            if (error.name === 'AbortError') {
                throw { status: 0, message: 'Request timeout' };
            }
            throw error;
        }
    }

    /**
     * Handle unload - send pending retries via sendBeacon
     */
    public unload(): void {
        this.retryQueue.unload();
    }

    private checkMonthlyLimit(): boolean {
        if (this.monthlyLimitReached) {
            return false;
        }
        return true;
    }

    private isThrottled(): boolean {
        return Date.now() < this.throttledUntil;
    }

    /**
     * A 429 is rate-limit throttling unless the body explicitly says the
     * monthly limit was hit. Throttling pauses sends for the server-provided
     * Retry-After window; persisted/queued events drain once it passes.
     */
    private async _apply429(response: Response): Promise<void> {
        let body: any = null;
        try {
            body = await response.clone().json();
        } catch {
            // Non-JSON body — treat as plain throttling.
        }
        if (body && body.monthlyLimitReached === true) {
            this.monthlyLimitReached = true;
            return;
        }
        const headerValue = response.headers?.get?.('Retry-After');
        const seconds = Number(headerValue ?? body?.retryAfterSeconds);
        const waitSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 300) : 30;
        this.throttledUntil = Date.now() + waitSeconds * 1000;
        logInfo(`Rate limited (429), pausing sends for ${waitSeconds}s`);
    }

    public async init(sessionId: string, userId: string | null) {
        // Check if monthly limit is already reached - silently skip if so
        if (!this.checkMonthlyLimit()) {
            // Silently return success to avoid any errors
            return {
                sessionId: sessionId,
                endUserId: userId
            };
        }

        // Get current page URL and referrer if in browser environment
        let entryURL = null;
        let referrer = null;
        
        if (typeof window !== 'undefined') {
            entryURL = window.location.href;
            referrer = document.referrer;
        }

        logInfo('API init called with:', { sessionId, userId, entryURL, referrer, baseUrl: this.baseUrl });

        try {
        const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/init`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.apiKey}`,
                'Referer': referrer || ''
            },
            body: safeJsonStringify({
                sessionId: sessionId,
                endUserId: userId,
                entryURL: entryURL,
                referrer: referrer,
                sdkVersion: SDK_VERSION // Include SDK version for tracking
            })
        });

            logInfo('API init response status:', response.status);

        if (!response.ok) {
            if (response.status === 429) {
                await this._apply429(response);
                // Silently return success to avoid any errors
                return {
                    sessionId: sessionId,
                    endUserId: userId
                };
            }
            const errorText = await response.text();
            logError('API init failed:', response.status, errorText);
            throw new Error(`Failed to initialize ingestion: ${response.statusText} - ${errorText}`);
        } 

        const responseJson = await response.json();
        
        // Check for monthly limit flag in successful response
        if (responseJson.monthlyLimitReached === true) {
            this.monthlyLimitReached = true;
            logInfo('Monthly limit reached detected from server response');
        }
        
        logInfo('API init success:', responseJson);
        return {
            sessionId: responseJson.sessionId,
            endUserId: responseJson.endUserId
        };
        } catch (error) {
            logError('API init error:', error);
            throw error;
        }
    }

    /**
     * Server detects IP from HTTP requests automatically
     */

    async sendEvents(events: any[], sessionId: string, userId: string) {
        // ✅ SIMPLE VALIDATION FOR ALL EVENTS
        const validEvents = events.filter(event => event && typeof event === 'object');
        
        const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/events`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.apiKey}`
            },
            body: safeJsonStringify({
                sessionId,
                events: validEvents,
                endUserId: userId,
                sdkVersion: SDK_VERSION // Include SDK version for tracking
            })
        });
        
        if (!response.ok) {
            if (response.status === 429) {
                await this._apply429(response);
                throw new Error(`429: rate limited`);
            }
            throw new Error(`Failed to send events: ${response.statusText}`);
        }
        
        // Check for monthly limit flag in successful response
        const responseJson = await response.json();
        if (responseJson.monthlyLimitReached === true) {
            this.monthlyLimitReached = true;
            logInfo('Monthly limit reached detected from events response');
        }
    }
    
    async sendEventsChunked(events: any[], sessionId: string, userId?: string, windowId?: string, automaticProperties?: any) {
        // Check if monthly limit is already reached - silently skip if so
        if (!this.checkMonthlyLimit()) {
            // Silently return success to avoid any errors
            return [];
        }
        try {
            const results = [];
            let currentChunk: any[] = [];
            // Tracks currentChunk's serialized size incrementally. Must be kept
            // in step with every mutation of currentChunk below — add() on
            // push, reset() on cut.
            const chunkBytes = createChunkByteCounter(sessionId);

            for (const event of events) {
                // ✅ SIMPLE VALIDATION FOR ALL EVENTS
                if (!event || typeof event !== 'object') {
                    continue;
                }

                if (chunkBytes.wouldExceed(event)) {
                    // If current chunk is not empty, send it first
                    if (currentChunk.length > 0) {
                        logDebug(`[SDK] Sending chunk with ${currentChunk.length} events`);
                        const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/events`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${this.apiKey}`
                            },
                            body: safeJsonStringify({
                                sessionId,
                                events: currentChunk,
                                endUserId: userId,
                                windowId: windowId,
                                automaticProperties: automaticProperties, // Include automatic properties for user creation
                                sdkVersion: SDK_VERSION // Include SDK version for tracking
                            })
                        });
                        
                        if (!response.ok) {
                            if (response.status === 429) {
                                await this._apply429(response);
                                // Throw so the outer catch persists the events for retry
                                throw new Error(`429: rate limited`);
                            }
                            throw new Error(`Failed to send events: ${response.statusText}`);
                        }
                        
                        const responseJson = await response.json();
                        
                        // Check for monthly limit flag in successful response
                        if (responseJson.monthlyLimitReached === true) {
                            this.monthlyLimitReached = true;
                            logInfo('Monthly limit reached detected from chunked events response');
                        }
                        
                        results.push(responseJson);
                        currentChunk = [];
                    }

                    // Handle large events by splitting them
                    const splitEvents = splitLargeEvent(event, sessionId);

                    // Start new chunk with the split events. Note this is the
                    // split result, not [event] — and as before, an oversized
                    // splitEvents is NOT re-split, it just becomes the chunk.
                    // Preserved deliberately: changing it would change payloads.
                    currentChunk = splitEvents;
                    chunkBytes.reset(splitEvents);
                } else {
                    // Add event to current chunk
                    currentChunk.push(event);
                    chunkBytes.add(event);
                }
            }
            
            // Send any remaining events
            if (currentChunk.length > 0) {
                const result = await this._sendChunkWithRetry(
                    currentChunk,
                    sessionId,
                    userId,
                    windowId,
                    automaticProperties || currentChunk[0]?.automaticProperties
                );
                if (result) {
                    results.push(result);
                }
            }
            
            return results.flat();
        } catch (error) {
            logError('Error sending events:', error);
            // Persist failed events for retry
            this._persistEvents(events, sessionId, userId, windowId, automaticProperties);
            throw error;
        }
    }

    /**
     * Send a chunk of events with retry logic and 413 handling
     */
    private async _sendChunkWithRetry(
        chunk: any[],
        sessionId: string,
        userId?: string,
        windowId?: string,
        automaticProperties?: any
    ): Promise<any | null> {
        let batchSize = Math.min(this.currentBatchSize, chunk.length);
        let startIndex = 0;

        while (startIndex < chunk.length) {
            const batch = chunk.slice(startIndex, startIndex + batchSize);
            const payload = {
                sessionId,
                events: batch,
                endUserId: userId,
                windowId: windowId,
                automaticProperties: automaticProperties,
                sdkVersion: SDK_VERSION // Include SDK version for tracking
            };

            const bodyString = safeJsonStringify(payload);
            const estimatedSize = new TextEncoder().encode(bodyString).length;

            try {
                const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/events`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.apiKey}`
                    },
                    body: bodyString
                }, estimatedSize);
                
                if (!response.ok) {
                    if (response.status === 429) {
                        await this._apply429(response);
                        // Persist remaining events
                        this._persistEvents(chunk.slice(startIndex), sessionId, userId, windowId, automaticProperties);
                        return null;
                    }
                    
                    if (response.status === 413) {
                        // Content too large - reduce batch size and retry
                        logWarn(`413 error: reducing batch size from ${batchSize} to ${Math.max(1, Math.floor(batchSize / 2))}`);
                        this.currentBatchSize = Math.max(1, Math.floor(batchSize / 2));
                        batchSize = this.currentBatchSize;
                        // Retry with smaller batch
                        continue;
                    }

                    // For other errors, use retry queue
                    await this.retryQueue.retriableRequest({
                        url: `${this.baseUrl}/api/ingestion/events`,
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${this.apiKey}`
                        },
                        body: bodyString,
                        estimatedSize: estimatedSize,
                        callback: (response) => {
                            if (response.statusCode === 200 && response.json) {
                                if (response.json.monthlyLimitReached === true) {
                                    this.monthlyLimitReached = true;
                                }
                            }
                        }
                    });
                    
                    // Persist for retry
                    this._persistEvents(chunk.slice(startIndex), sessionId, userId, windowId, automaticProperties);
                    return null;
                }
                
                const responseJson = await response.json();
                
                // Check for monthly limit flag in successful response
                if (responseJson.monthlyLimitReached === true) {
                    this.monthlyLimitReached = true;
                    logInfo('Monthly limit reached detected from chunked events response');
                }
                
                startIndex += batchSize;
                
                // If we successfully sent a batch, return the result
                if (startIndex >= chunk.length) {
                    return responseJson;
                }
            } catch (error: any) {
                // Network error - use retry queue
                logWarn('Network error sending chunk, adding to retry queue:', error);
                await this.retryQueue.retriableRequest({
                    url: `${this.baseUrl}/api/ingestion/events`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.apiKey}`
                    },
                    body: bodyString,
                    estimatedSize: estimatedSize,
                    callback: (response) => {
                        if (response.statusCode === 200 && response.json) {
                            if (response.json.monthlyLimitReached === true) {
                                this.monthlyLimitReached = true;
                            }
                        }
                    }
                });
                
                // Persist for retry
                this._persistEvents(chunk.slice(startIndex), sessionId, userId, windowId, automaticProperties);
                return null;
            }
        }

        return null;
    }

    /**
     * Persist events to storage for retry
     */
    private _persistEvents(
        events: any[],
        sessionId: string,
        userId?: string,
        windowId?: string,
        automaticProperties?: any
    ): void {
        if (events.length === 0) {
            return;
        }

        this.persistence.addToQueue({
            sessionId,
            events,
            endUserId: userId,
            windowId,
            automaticProperties,
            timestamp: Date.now()
        });
    }

    async sendUserData(
        userId: string,
        userData: Record<string, any>,
        sessionId: string,
        identityToken?: string | null
    ) {
        try {
            const payload: Record<string, any> = {
                userId: userId,
                userAttributes: userData,
                sessionId: sessionId,
                posthogName: userData.email || userData.name || null // Update user name with email
            };
            // Signed by the customer's backend for a user it has already
            // authenticated. The API key only proves the project, so this is
            // what lets the server trust an identity claim.
            if (identityToken) {
                payload.identityToken = identityToken;
            }
            
            logDebug('Sending user data to server:', { ...payload, identityToken: identityToken ? '[redacted]' : undefined });
            
            const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body: safeJsonStringify(payload)
            });
            
            if (!response.ok) {
                // Identity verification rejections carry the actionable detail
                // ("requires a signed identityToken", "expired", ...). Surfacing
                // only the status text left customers with a bare "Forbidden".
                const detail = await response.text().then(
                    (text) => {
                        try {
                            return JSON.parse(text)?.error ?? text;
                        } catch {
                            return text;
                        }
                    },
                    () => ''
                );
                throw new Error(
                    `Failed to send user data: ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`
                );
            }
            
            const result = await response.json();
            logDebug('Server response:', result);
            return result;
        } catch (error) {
            logError('Error sending user data:', error);
            throw error;
        }
    }

    /**
     * Fire a tiny, fire-and-forget beacon to evict this session from the
     * dashboard's live-presence set. Called from the SDK's `pagehide`
     * handler. Pass the current `endUserId` (when known) so the server can
     * evict the user-keyed entry — multi-device/multi-tab presence is
     * folded onto a single entry on the server side.
     *
     * We intentionally do NOT await or check the response: the page is
     * unloading and the indicator is best-effort. If the beacon never
     * fires (older browsers, mobile force-quit, browser crash), the entry
     * still ages out of the live set within ~LIVE_WINDOW_MS on the server.
     */
    public sendSessionEndBeacon(sessionId: string, endUserId?: string | null): boolean {
        try {
            const payload = {
                sessionId,
                endUserId: endUserId || null,
                apiKey: this.apiKey,
            };
            const blob = new Blob([safeJsonStringify(payload)], {
                type: BEACON_MIME,
            });
            return sendKeepaliveBeacon(`${this.baseUrl}/api/ingestion/session-end`, blob);
        } catch {
            return false;
        }
    }

    /**
     * Periodic presence ping. Fires from a setInterval inside the SDK
     * tracker (~30s while the tab is visible) so an idle tab — no DOM
     * mutations, no input, empty rrweb queue — still refreshes its score
     * in the dashboard's live set. Without this, a perfectly static page
     * with a still user would drop out at LIVE_WINDOW_MS even though the
     * tab is open. Fire-and-forget; failures are silent.
     */
    public sendHeartbeatBeacon(sessionId: string, endUserId?: string | null): boolean {
        try {
            const payload = {
                sessionId,
                endUserId: endUserId || null,
                apiKey: this.apiKey,
            };
            const blob = new Blob([safeJsonStringify(payload)], {
                type: BEACON_MIME,
            });
            return sendKeepaliveBeacon(`${this.baseUrl}/api/ingestion/heartbeat`, blob);
        } catch {
            return false;
        }
    }

    public sendBeaconEvents(events: any[], sessionId: string, userId?: string, windowId?: string, automaticProperties?: any, groups?: Record<string, string>) {
        // Create JSON payload that matches the server's expected format
        // ✅ FIX: Include all fields that sendEventsChunked includes
        // This ensures sendBeacon requests are processed identically to regular HTTP requests
        const payload = {
            sessionId: sessionId,
            events: events,
            endUserId: userId || null, // ✅ FIX: Use actual userId instead of hardcoded null
            windowId: windowId, // ✅ FIX: Include windowId if available
            automaticProperties: automaticProperties, // ✅ FIX: Include automatic properties for user creation
            sdkVersion: SDK_VERSION, // ✅ FIX: Include SDK version for tracking
            apiKey: this.apiKey // Include API key in body since beacon can't use headers
        };

        // Convert to Blob for sendBeacon
            const blob = new Blob([safeJsonStringify(payload)], {
            type: BEACON_MIME
        });

        const success = navigator.sendBeacon(
            `${this.baseUrl}/api/ingestion/events`, 
            blob
        );

        return success;
    }

    async sendCustomEvent(sessionId: string, eventName: string, eventProperties?: Record<string, any>, endUserId?: string | null, eventId?: string) {
        logInfo('[SDK] Sending custom event', { sessionId, eventName, eventProperties, endUserId });
        try {
            const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/customEvent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body: safeJsonStringify({
                    sessionId: sessionId,
                    eventName: eventName,
                    eventProperties: eventProperties || {},
                    endUserId: endUserId || null,
                    // Idempotency ID (same one the batch attempt carried), so
                    // the server drops this if the batch was already accepted.
                    ...(eventId ? { eventId } : {})
                })
            });
            
            logInfo('[SDK] Custom event response', { status: response.status, statusText: response.statusText });
            
            if (!response.ok) {
                const errorText = await response.text();
                logError('[SDK] Failed to send custom event', { status: response.status, statusText: response.statusText, errorText });
                throw new Error(`Failed to send custom event: ${response.status} ${response.statusText} - ${errorText}`);
            }
            
            const json = await response.json();
            logDebug('[SDK] Custom event success', json);
            return json;
        } catch (error) {
            logError('[SDK] Error sending custom event', error, { sessionId, eventName, eventProperties });
            throw error;
        }
    }

    async sendCustomEventBatch(sessionId: string, events: Array<{ eventName: string; eventProperties?: Record<string, any>; eventId?: string }>, endUserId?: string | null) {
        const body = safeJsonStringify({
            sessionId: sessionId,
            events: events,
            endUserId: endUserId || null
        });
        try {
            const response = await this.trackedFetch(`${this.baseUrl}/api/ingestion/customEvent/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body
            });
            
            if (!response.ok) {
                throw new Error(`Failed to send custom event batch: ${response.statusText}`);
            }
            
            return await response.json();
        } catch (error) {
            // No retry-queue here on purpose: the tracker already falls back
            // to per-event sends (and ultimately the durable rrweb queue) when
            // the batch fails; adding a queued retry would double-send.
            logError('Error sending custom event batch:', error);
            throw error;
        }
    }

    public sendCustomEventBatchBeacon(
        sessionId: string,
        events: Array<{ eventName: string; eventProperties?: Record<string, any>; eventId?: string }>,
        endUserId?: string | null
    ): boolean {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
            return false;
        }
        try {
            const payload = {
                sessionId,
                events,
                endUserId: endUserId || null,
                apiKey: this.apiKey
            };
            const blob = new Blob([safeJsonStringify(payload)], {
                type: BEACON_MIME
            });
            return navigator.sendBeacon(
                `${this.baseUrl}/api/ingestion/customEvent/batch`,
                blob
            );
        } catch {
            return false;
        }
    }

    /**
     * Send console log (warn/error) to ingestion server
     */
    async sendLog(logData: {
        eventId?: string;
        level: 'warn' | 'error';
        message: string;
        stack?: string;
        url: string;
        environment?: string | null;
        timestampMs: number;
        sessionId: string;
        endUserId: string | null;
        automaticProperties?: Record<string, unknown>;
    }): Promise<void> {
        try {
            logDebug('[SDK] Sending log to server:', { level: logData.level, message: logData.message.substring(0, 50), sessionId: logData.sessionId });
            
            if (!this.baseUrl) {
                return;
            }
            
            if (!logData.sessionId) {
                return;
            }

            // Routed through the retry queue (like sendError) so a transient
            // network failure / 5xx retries with backoff instead of silently
            // dropping the log. Uses plain fetch internally, so this request
            // is never re-captured as a network error.
            const body = safeJsonStringify(logData);
            await this.retryQueue.retriableRequest({
                url: `${this.baseUrl}/api/ingestion/logs`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body,
                estimatedSize: typeof body === 'string' ? body.length : 0
            });
        } catch (error) {
            // Silent fail - don't break app if logging fails
            logWarn('[SDK] Failed to send log to server:', error);
        }
    }

    /**
     * Send network error to ingestion server
     */
    async sendNetworkError(errorData: {
        requestId: string;
        url: string;
        method: string;
        status: number | null;
        statusText: string | null;
        duration: number;
        timestampMs: number;
        sessionId: string;
        endUserId: string | null;
        errorType: string;
        errorMessage: string | null;
        errorName?: string | null;
        // New span fields
        startTimeMs?: number;
        spanName?: string;
        spanStatus?: 'error' | 'success' | 'slow';
        attributes?: Record<string, any>;
        automaticProperties?: Record<string, unknown>;
    }): Promise<void> {
        try {
            logDebug('[SDK] Sending network error to server:', { errorType: errorData.errorType, url: errorData.url.substring(0, 50), sessionId: errorData.sessionId });
            
            if (!this.baseUrl) {
                return;
            }
            
            if (!errorData.sessionId) {
                return;
            }
            
            // Use regular fetch (not trackedFetch) since this is SDK's own request
            const response = await fetch(`${this.baseUrl}/api/ingestion/network`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body: safeJsonStringify(errorData)
            });
            
            if (!response.ok) {
                logWarn('[SDK] Failed to send network error to server:', response.status, response.statusText);
            } else {
                logDebug('[SDK] Network error sent successfully');
            }
        } catch (error) {
            // Silent fail - don't break app if tracking fails
            logWarn('[SDK] Failed to send network error to server:', error);
        }
    }

    /**
     * Send a batch of tracing spans to the ingestion server. Fire-and-forget,
     * fails silently. Uses a plain `fetch` (the SDK's own request must not be
     * re-captured) and posts to the spans batch endpoint.
     */
    async sendSpans(
        spans: unknown[],
        ctx: { sessionId: string | null; endUserId: string | null; automaticProperties?: Record<string, unknown> },
    ): Promise<void> {
        try {
            if (!this.baseUrl || !ctx.sessionId || !spans || spans.length === 0) return;
            // Retry queue (like sendError): a transient failure must not drop
            // a whole span batch, which would silently distort every Traces /
            // Insights aggregate built on it.
            const body = safeJsonStringify({
                sessionId: ctx.sessionId,
                endUserId: ctx.endUserId,
                automaticProperties: ctx.automaticProperties,
                spans,
            });
            await this.retryQueue.retriableRequest({
                url: `${this.baseUrl}/api/ingestion/spans/batch`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`,
                },
                body,
                estimatedSize: typeof body === 'string' ? body.length : 0,
            });
        } catch (error) {
            logWarn('[SDK] Failed to send spans:', error);
        }
    }

    /** sendBeacon variant of `sendSpans` for page unload (synchronous). */
    public sendSpansBeacon(
        spans: unknown[],
        ctx: { sessionId: string | null; endUserId: string | null; automaticProperties?: Record<string, unknown> },
    ): boolean {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
        if (!this.baseUrl || !ctx.sessionId || !spans || spans.length === 0) return false;
        try {
            const blob = new Blob(
                [
                    safeJsonStringify({
                        sessionId: ctx.sessionId,
                        endUserId: ctx.endUserId,
                        automaticProperties: ctx.automaticProperties,
                        spans,
                        apiKey: this.apiKey,
                    }),
                ],
                { type: BEACON_MIME },
            );
            return navigator.sendBeacon(`${this.baseUrl}/api/ingestion/spans/batch`, blob);
        } catch {
            return false;
        }
    }

    /**
     * Send a captured crash/error report to the ingestion server.
     *
     * Routed through the retry queue (not a bare fire-and-forget `fetch`) so a
     * transient 5xx / network failure is retried with backoff instead of losing
     * the crash — and anything still queued is flushed via sendBeacon on unload.
     * The queue does not retry 4xx (except 408/429), which is the correct
     * behaviour for a malformed report. Retrying a duplicate is safe: the SDK
     * dedups within a window and the server groups by fingerprint. The queue
     * uses a plain `fetch` internally, so this request is never re-captured as a
     * network error. Best-effort and never throws into the host app.
     */
    async sendError(report: ErrorReport): Promise<void> {
        try {
            logDebug('[SDK] Sending error to server:', {
                exceptionType: report.exceptionType,
                mechanism: report.mechanism,
                sessionId: report.sessionId,
            });

            if (!this.baseUrl || !report.sessionId) {
                return;
            }

            const body = safeJsonStringify(report);
            await this.retryQueue.retriableRequest({
                url: `${this.baseUrl}/api/ingestion/errors`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body,
                estimatedSize: typeof body === 'string' ? body.length : 0
            });
        } catch (error) {
            // Silent fail - don't break app if error reporting fails
            logWarn('[SDK] Failed to send error to server:', error);
        }
    }

    /**
     * Trigger server-side GeoIP enrichment. Server resolves the IP from request
     * headers; result is published as a $geoip analytics event that updates
     * raw_sessions.country/city/region in ClickHouse.
     */
    async sendIpInfo(sessionId: string, endUserId: string | null): Promise<void> {
        try {
            if (!this.baseUrl || !sessionId || !endUserId) return;

            const response = await fetch(`${this.baseUrl}/api/ingestion/ip-info`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body: safeJsonStringify({
                    sessionId,
                    endUserId,
                    ipDetectionMethod: 'server_header'
                })
            });

            if (!response.ok) {
                logWarn('[SDK] Failed to send ip-info:', response.status, response.statusText);
            }
        } catch (error) {
            logWarn('[SDK] Failed to send ip-info:', error);
        }
    }

    /**
     * Wrapper for fetch that tracks network errors and falls back to sendBeacon on CSP violations
     * Skips tracking for SDK's own requests to ingestion server
     */
    private async trackedFetch(url: string, options: RequestInit, estimatedSize?: number): Promise<Response> {
        const requestStartTime = Date.now();
        const requestId = uuidv1();
        
        // ✅ SKIP TRACKING: Don't track SDK's own requests to ingestion server
        const shouldSkipTracking = this.shouldSkipNetworkTracking(url);
        
        // If CSP is already known to be blocking, use sendBeacon directly for POST requests
        if (this.cspBlocked && options.method === 'POST' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            return this.trackedFetchWithBeaconFallback(url, options, shouldSkipTracking);
        }
        
        try {
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            let timeoutId: ReturnType<typeof setTimeout> | null = null;

            if (controller) {
                timeoutId = setTimeout(() => {
                    controller!.abort();
                }, this.requestTimeout);
            }

            const useKeepalive = options.method === 'POST' && estimatedSize !== undefined && estimatedSize < KEEP_ALIVE_THRESHOLD;

            const response = await fetch(url, {
                ...options,
                signal: controller?.signal,
                keepalive: useKeepalive
            });

            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            const requestDuration = Date.now() - requestStartTime;
            this.consecutiveFetchFailures = 0; // fetch works; clear CSP probation
            
            // Track failed requests (4xx, 5xx) AND skip SDK requests
            if (!response.ok && !shouldSkipTracking) {
                await this.sendNetworkError({
                    requestId,
                    url,
                    method: options.method || 'GET',
                    status: response.status,
                    statusText: response.statusText,
                    duration: requestDuration,
                    timestampMs: Date.now(),
                    sessionId: this.sessionId,
                    endUserId: this.endUserId,
                    errorType: this.classifyHttpError(response.status),
                    errorMessage: response.statusText,
                    // New span fields
                    startTimeMs: requestStartTime,
                    spanName: `${options.method || 'GET'} ${url}`,
                    spanStatus: 'error',
                    attributes: {
                        'http.status_code': response.status,
                        'http.status_text': response.statusText,
                    }
                }).catch(() => {}); // Non-blocking
            }
            
            return response;
        } catch (error: any) {
            const requestDuration = Date.now() - requestStartTime;
            
            // Handle timeout errors
            if (error.name === 'AbortError') {
                const timeoutError: any = new Error('Request timeout');
                timeoutError.name = 'TimeoutError';
                error = timeoutError;
            }
            
            // Check if this is a CSP violation
            const isCSPViolation = this.isCSPViolation(error);

            if (isCSPViolation && options.method === 'POST' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
                // "Failed to fetch" is indistinguishable from a transient
                // network blip, so the STICKY beacon-only mode needs
                // probation: only flip it after consecutive failures.
                // Flipping on the first failure permanently degrades the
                // session to fire-and-forget beacons (no retry, no server
                // confirmation, 64KB unload quota) — observed to drop
                // CLS/INP at page close after a single injected failure.
                this.consecutiveFetchFailures += 1;
                if (this.consecutiveFetchFailures >= 2) {
                    this.cspBlocked = true;
                    logWarn('[SDK] Repeated fetch failures (likely CSP), falling back to sendBeacon for future requests');
                }
                // One-shot beacon fallback for THIS request either way, so a
                // genuine CSP block still delivers.
                return this.trackedFetchWithBeaconFallback(url, options, shouldSkipTracking);
            }
            
            // Track network errors BUT skip SDK requests
            if (!shouldSkipTracking) {
                await this.sendNetworkError({
                    requestId,
                    url,
                    method: options.method || 'GET',
                    status: null,
                    statusText: null,
                    duration: requestDuration,
                    timestampMs: Date.now(),
                    sessionId: this.sessionId,
                    endUserId: this.endUserId,
                    errorType: this.classifyNetworkError(error),
                    errorMessage: error.message,
                    errorName: error.name,
                    // New span fields
                    startTimeMs: requestStartTime,
                    spanName: `${options.method || 'GET'} ${url}`,
                    spanStatus: 'error',
                    attributes: {
                        'error.name': error.name,
                        'error.message': error.message,
                    }
                }).catch(() => {}); // Non-blocking
            }
            
            throw error; // Re-throw to maintain existing error handling
        }
    }

    /**
     * Fallback to sendBeacon when CSP blocks fetch
     * sendBeacon bypasses CSP connect-src restrictions
     * Note: sendBeacon is synchronous and fire-and-forget, so we can't await it
     */
    private async trackedFetchWithBeaconFallback(url: string, options: RequestInit, shouldSkipTracking: boolean): Promise<Response> {
        try {
            // Extract and parse body synchronously (sendBeacon is synchronous)
            let bodyJson: any = null;
            let bodyString: string = '';
            
            if (options.body) {
                if (typeof options.body === 'string') {
                    try {
                        bodyJson = JSON.parse(options.body);
                        bodyString = options.body;
                    } catch {
                        // If not JSON, use as-is
                        bodyString = options.body;
                    }
                } else if (options.body instanceof Blob) {
                    // Can't read Blob synchronously, so we'll need to handle this differently
                    // For now, we'll add apiKey to URL and send the blob as-is
                    logWarn('[SDK] Cannot extract apiKey from Blob body for sendBeacon, using URL param');
                    url = `${url}${url.includes('?') ? '&' : '?'}apiKey=${encodeURIComponent(this.apiKey)}`;
                    const success = navigator.sendBeacon(url, options.body);
                    // JSON body required: callers `await response.json()` (see below).
                    return success ? new Response('{"ok":true,"transport":"sendBeacon"}', { status: 200, statusText: 'OK', headers: new Headers({ 'Content-Type': 'application/json' }) })
                                   : new Response('{"ok":false}', { status: 500, statusText: 'sendBeacon failed', headers: new Headers({ 'Content-Type': 'application/json' }) });
                } else {
                    // For other types, try to stringify
                    bodyString = safeJsonStringify(options.body);
                    bodyJson = options.body;
                }
            }
            
            // sendBeacon doesn't support headers, so we need to include auth in body or URL
            // For ingestion endpoints, include apiKey in the body JSON
            if (url.includes('/api/ingestion/')) {
                if (bodyJson && typeof bodyJson === 'object') {
                    // Add API key to body JSON
                    bodyJson.apiKey = this.apiKey;
                    bodyString = safeJsonStringify(bodyJson);
                } else if (bodyString) {
                    // Try to parse and add apiKey
                    try {
                        const parsed = JSON.parse(bodyString);
                        parsed.apiKey = this.apiKey;
                        bodyString = safeJsonStringify(parsed);
                    } catch {
                        // If parsing fails, append apiKey as query param
                        url = `${url}${url.includes('?') ? '&' : '?'}apiKey=${encodeURIComponent(this.apiKey)}`;
                    }
                } else {
                    // No body, add apiKey to URL
                    url = `${url}${url.includes('?') ? '&' : '?'}apiKey=${encodeURIComponent(this.apiKey)}`;
                }
            } else {
                // For non-ingestion endpoints, add apiKey to URL
                url = `${url}${url.includes('?') ? '&' : '?'}apiKey=${encodeURIComponent(this.apiKey)}`;
            }
            
            // Create Blob with proper Content-Type for sendBeacon
            const blob = bodyString 
                ? new Blob([bodyString], { type: BEACON_MIME })
                : null;
            
            // Use sendBeacon (synchronous, fire-and-forget)
            const success = navigator.sendBeacon(url, blob);

            // The synthetic Response MUST carry a JSON body: callers do
            // `await response.json()`, and a null body makes that throw,
            // which callers interpret as a failed send and re-send the same
            // payload through their fallback path — double-delivering every
            // event in the batch (double-counting bug caught by the chaos
            // harness).
            const syntheticBody = '{"ok":true,"transport":"sendBeacon"}';
            if (success) {
                logDebug('[SDK] Successfully sent request via sendBeacon (CSP fallback)');
                return new Response(syntheticBody, {
                    status: 200,
                    statusText: 'OK',
                    headers: new Headers({ 'Content-Type': 'application/json' })
                });
            } else {
                logWarn('[SDK] sendBeacon returned false - browser may be throttling');
                // Return success anyway since sendBeacon is best-effort
                return new Response(syntheticBody, {
                    status: 200,
                    statusText: 'OK (sendBeacon best-effort)',
                    headers: new Headers({ 'Content-Type': 'application/json' })
                });
            }
        } catch (error: any) {
            logError('[SDK] sendBeacon fallback failed:', error);
            // Return a mock error response
            return new Response(null, {
                status: 500,
                statusText: 'Failed to send via sendBeacon',
                headers: new Headers()
            });
        }
    }

    /**
     * Detect if an error is a CSP violation
     */
    private isCSPViolation(error: any): boolean {
        const errorMessage = (error?.message || '').toLowerCase();
        const errorName = (error?.name || '').toLowerCase();
        
        // CSP violations typically manifest as:
        // 1. TypeError with "Failed to fetch" and CSP-related text
        // 2. Network errors that mention CSP or Content Security Policy
        // 3. Errors that mention "violates" and "Content Security Policy"
        
        return (
            (errorName === 'typeerror' && errorMessage.includes('failed to fetch')) ||
            errorMessage.includes('content security policy') ||
            errorMessage.includes('csp') ||
            errorMessage.includes('violates') ||
            // Check for common CSP violation patterns
            (errorMessage.includes('refused to connect') && errorMessage.includes('violates'))
        );
    }

    /**
     * Check if network request should be skipped (SDK's own requests)
     */
    private shouldSkipNetworkTracking(url: string): boolean {
        // Skip tracking if URL matches ingestion server base URL
        if (!url || !this.baseUrl) {
            return false;
        }
        
        try {
            const urlObj = new URL(url);
            const baseUrlObj = new URL(this.baseUrl);
            
            // Skip if same origin (same protocol, host, port)
            if (urlObj.origin === baseUrlObj.origin) {
                // Also check if it's an ingestion endpoint
                if (urlObj.pathname.startsWith('/api/ingestion/')) {
                    return true;
                }
            }
            
            // Also check string matching as fallback
            if (url.includes(this.baseUrl)) {
                return true;
            }
            
            return false;
        } catch (error) {
            // If URL parsing fails, do simple string check
            return url.includes(this.baseUrl);
        }
    }

    private classifyHttpError(status: number): string {
        if (status >= 400 && status < 500) {
            return 'client_error';
        }
        if (status >= 500) {
            return 'server_error';
        }
        return 'unknown_error';
    }

    private classifyNetworkError(error: any): string {
        const errorMessage = error.message || '';
        const errorName = error.name || '';
        
        // Check for CSP violations first
        if (this.isCSPViolation(error)) {
            return 'csp_violation';
        }
        
        // Check for blocked requests (ad blockers, browser extensions, etc.)
        // This includes ERR_BLOCKED_BY_CLIENT, ERR_BLOCKED_BY_RESPONSE, and other blocked variants
        if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT') || 
            errorMessage.includes('ERR_BLOCKED_BY_RESPONSE') ||
            errorMessage.includes('blocked:other') ||
            errorMessage.includes('net::ERR_BLOCKED_BY_CLIENT') ||
            errorMessage.includes('net::ERR_BLOCKED_BY_RESPONSE') ||
            (errorName === 'TypeError' && errorMessage.includes('Failed to fetch') && 
             (errorMessage.includes('blocked') || errorMessage.includes('ERR_BLOCKED')))) {
            return 'blocked_by_client';
        }
        if (errorMessage.includes('CORS') || errorMessage.includes('Access-Control')) {
            return 'cors_error';
        }
        if (errorMessage.includes('timeout') || errorName === 'TimeoutError') {
            return 'timeout_error';
        }
        if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
            return 'network_error';
        }
        return 'unknown_error';
    }
}